<template>
    <div :id="`Question-${order}`" class="card quiz-question mb-5">
        <header class="card-header">
            <media-draggable
                class="card-header-title"
                :open="expanded"
                :disabled="hasUnsavedQuestions"
                can-open
                :toggle-callback="() => set('expanded', !expanded)"
            >
                <template #title>
                    <div class="tw:relative">
                        <span>Question {{ Number(order) + 1 }}: {{ typeLabel }}</span>
                        <div v-show="invalid" class="tw:absolute tw:top-full tw:left-0">
                            <auto-save :type="{ global: false, header: true }" :status="validator" />
                        </div>
                    </div>
                </template>

                <template #actions>
                    <div class="tw:flex tw:flex-col tw:gap-2 tw:items-center">
                        <div v-if="!survey">
                            <e-tag>
                                {{ totalPoints }}
                                {{ translate('point', totalPoints) }}
                                Possible
                            </e-tag>
                        </div>

                        <div class="tw:mt-4">
                            <e-button
                                type="is-danger"
                                outlined
                                title="Delete Question"
                                :disabled="isQuizAiQuizGenerationResult"
                                size="small"
                                @click="remove"
                                @keyup="remove"
                            >
                                <e-icon icon="trash-alt" />
                            </e-button>
                        </div>
                    </div>
                </template>
            </media-draggable>
        </header>

        <div v-if="expanded" class="card-content">
            <div class="columns">
                <div class="column">
                    <e-field>
                        <template #label>
                            <div>Question *</div>

                            <auto-save
                                v-show="validator.question"
                                :type="{
                                    global: false,
                                    header: false,
                                    list: true,
                                }"
                                :status="validator.question"
                            />
                        </template>
                        <rich-text-editor v-model="body" enable-wrapper placeholder="Type question here..." />
                    </e-field>
                </div>
            </div>

            <div class="columns is-vcentered">
                <div class="column">
                    <div class="label">Optional Settings</div>
                    <div v-if="survey" class="columns m-b-xs">
                        <div class="column">
                            <e-field class="m-t-smd is-align-center">
                                <e-checkbox v-model="isRepetitive">
                                    <div class="level-left">
                                        <div class="level-item">
                                            Autofill the response with the student's previous response within the same
                                            course.
                                        </div>
                                        <div class="level-item">
                                            <e-tooltip
                                                multilined
                                                label="Autofill will only occur when the student, question, and course are identical."
                                            >
                                                <e-icon icon="question-circle" />
                                            </e-tooltip>
                                        </div>
                                    </div>
                                </e-checkbox>
                            </e-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div v-if="survey" class="column is-flex">
                            <e-field v-if="type === 'free_form'" label="Minimum Word Count" class="m-r-lg">
                                <e-input v-model="minimumWordCount" type="number" />
                            </e-field>

                            <e-field class="m-t-smd is-align-center">
                                <e-checkbox v-model="required" :true-value="false" false-value>
                                    Check to allow question to be skipped
                                </e-checkbox>
                            </e-field>
                        </div>

                        <div v-else class="column is-flex-vertical">
                            <e-field>
                                <e-checkbox v-model="random">
                                    Arrange in Answers in Random Order on Every Quiz Attempt
                                </e-checkbox>
                            </e-field>

                            <e-field v-if="multipleAnswer" :message="partialCreditLabel" type="is-info">
                                <e-checkbox v-model="partial">Allow Partial Credit</e-checkbox>
                            </e-field>

                            <div v-if="multipleAnswer && !partial" style="width: 15rem" class="m-t-lg">
                                <e-field
                                    :type="{
                                        'is-danger': invalidStep,
                                        'is-warning': questionPoints < 0,
                                    }"
                                    horizontal
                                >
                                    <template #message>
                                        <div v-if="invalidStep || questionPoints < 0" class="has-text-danger">
                                            <e-icon icon="exclamation-triangle" />
                                            <span>
                                                {{ inputMessage }}
                                            </span>
                                        </div>
                                    </template>
                                    <template #label>
                                        <div style="min-width: 7rem">Total Points</div>
                                    </template>

                                    <e-input v-model="questionPoints" :step="validStep" type="number" min="0" />
                                </e-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="answers" class="columns">
                <div class="column">
                    <quiz-answers
                        :answers="answers"
                        :details="{ uuid, type, parent: order }"
                        :multiple-choice="multipleChoice"
                        @add-answer="add"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty.js';
import some from 'lodash/some.js';

import { useI18n } from 'vue-i18n';
import { createNamespacedHelpers } from 'vuex';

import AutoSave from '@/components/auto-save/AutoSave.vue';
// Import Editor component lazily
import Editor from '@/components/editor/Editor.vue';
import MediaDraggable from '@/components/elements/MediaDraggable.vue';
import QuizAnswers from '@/components/quiz-builder/quiz-answers/QuizAnswers.vue';
import QuizTypeMixin from '@/mixins/QuizType.mixin.js';

const { mapState } = createNamespacedHelpers('QuizBuilder');

const field = name => {
    return {
        get() {
            return this.get(name);
        },
        set(value) {
            this.set(name, value);
        },
    };
};

export default {
    name: 'QuizQuestions',

    components: {
        AutoSave,
        QuizAnswers,
        RichTextEditor: Editor,
        MediaDraggable,
    },

    mixins: [QuizTypeMixin],

    props: {
        uuid: {
            type: [String, Number],
            required: true,
        },

        order: {
            type: [String, Number],
            required: true,
        },

        isQuizAiQuizGenerationResult: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['add-answer', 'incomplete-question'],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        validStep: 0.25,
        isEditorReady: false,
    }),

    computed: {
        ...mapState({
            typeLabel(state) {
                if (!state.question_types || !Array.isArray(state.question_types)) {
                    return this.type || 'Unknown';
                }

                const index = state.question_types.findIndex(item => item.value === this.type);

                if (index === -1 || !state.question_types[index]) {
                    return this.type || 'Unknown';
                }

                return state.question_types[index].name;
            },
        }),

        minimumWordCount: field('minimum_word_count'),

        random: field('random_answer_order'),

        partial: field('partial_credit'),

        questionPoints: field('points'),

        required: field('required'),

        isRepetitive: field('is_repetitive'),

        answers: field('answers'),

        body: field('body'),

        hint: field('hint'),

        type: field('type'),

        id: field('id'),

        chevron() {
            return this.expanded ? 'angle-up' : 'angle-down';
        },

        expanded() {
            return this.get('expanded');
        },

        multipleChoice() {
            return this.type.includes('multiple');
        },

        multipleAnswer() {
            return this.type === 'multiple_choice_multiple_answer';
        },

        points() {
            return this.answers.filter(item => item.is_correct).reduce((total, item) => total + Number(item.value), 0);
        },

        invalid() {
            this.$emit(
                'incomplete-question',
                some(this.validator, item => !isEmpty(item))
            );

            return some(this.validator, item => !isEmpty(item));
        },

        validator() {
            return this.$store.getters[`QuizBuilder/${this.uuid}/validate`]({
                target: this.order,
                type: 'question',
            });
        },

        partialCreditLabel() {
            return this.partial ? this.translate('partial_credit') : this.translate('no_partial_credit');
        },

        totalPoints() {
            if (!this.multipleAnswer) return this.points;

            return this.partial ? this.points : this.questionPoints;
        },

        invalidStep() {
            return this.questionPoints % this.validStep !== 0;
        },

        inputMessage() {
            if (this.invalidStep)
                return this.translate('multiples_of_are_accepted', {
                    step: this.validStep,
                });

            if (this.questionPoints < 0) return this.translate('recommended_value');

            return '';
        },

        hasUnsavedQuestions() {
            return this.$store.getters[`QuizBuilder/${this.uuid}/hasUnsavedQuestions`]();
        },
    },

    mounted() {
        // Initialize editor after component is mounted
        this.$nextTick(() => {
            this.isEditorReady = true;
        });
    },

    methods: {
        add() {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/addAnswer`, {
                target: this.order,
            });
        },

        get(name) {
            return this.$store.getters[`QuizBuilder/${this.uuid}/question`]({
                order: this.order,
                field: name,
            });
        },

        remove() {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/confirmRemoval`, {
                type: 'questions',
                target: this.order,
                store: this.$store,
            });
        },

        set(name, value) {
            this.$store.dispatch(`QuizBuilder/${this.uuid}/setQuestionField`, {
                order: this.order,
                field: name,
                value,
            });
        },
    },
};
</script>

<style></style>
